import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ServerDomainInfo } from '@/api/type-server'
import { Button, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { FilterItem } from '@/hooks/useInstanceList'
import { MemberFilter } from '@/pages/teams/components/MemberFilter'
import { formatDomain } from '@/utils/formatter'

interface AdminFiltersProps {
  // Member filter
  selectedMembers: string[]
  onMembersChange: (ids: string[]) => void

  // Status filter
  statusFilters: FilterItem[]
  selectedStatuses: (string | number)[]
  onStatusChange: (statuses: (string | number)[]) => void

  // Region filter
  domainInfos: ServerDomainInfo[]
  region: number
  onRegionChange: (region: number, regions: number[]) => void
  REGION_GLOBAL: number
  REGION_DATA_DRIVE: number
  REGION_MESH_CLUSTERING: number

  // Bulk actions
  selectedCount: number
  allCanRelease: boolean
  onBulkEdit: (action: 'release') => void
}

export function AdminFilters({
  selectedMembers,
  onMembersChange,
  statusFilters,
  selectedStatuses,
  onStatusChange,
  domainInfos,
  region,
  onRegionChange,
  REGION_GLOBAL,
  REGION_DATA_DRIVE,
  REGION_MESH_CLUSTERING,
  selectedCount,
  allCanRelease,
  onBulkEdit,
}: AdminFiltersProps) {
  const { t } = useTranslation()

  const statusOptions = statusFilters.map((filter) => ({
    label: filter.label,
    value: filter.value,
    className: 't-sm',
  }))

  const regionOptions = [
    { label: t('common.region.groupGlobal'), value: REGION_GLOBAL, className: 't-sm' },
    { label: t('common.region.groupDataDrive'), value: REGION_DATA_DRIVE, className: 't-sm' },
    {
      label: t('common.region.groupMeshClustering'),
      value: REGION_MESH_CLUSTERING,
      className: 't-sm',
    },
    ...domainInfos.map((info, index) => ({
      label: info.displayName || formatDomain(info.domain),
      value: info.domain,
      className: 't-sm',
      separatorBefore: index === 0,
    })),
  ]

  const bulkEditOptions = [
    {
      label: t('team.admin.bulkEdit.release', 'Release'),
      value: 'release',
      className: 't-sm text-error',
    },
  ]

  return (
    <div className="mb-4">
      <div className="flex gap-6 items-end mb-4">
        {/* Member Filter */}
        <MemberFilter value={selectedMembers} onChange={onMembersChange} />

        {/* Instance Status Filter */}
        <div className="flex flex-col gap-2">
          <div className="text-secondary t-sb">
            <Trans i18nKey="instance.list.statusLabel">Instance status</Trans>
          </div>
          <Dropdown
            rootClassName="t-sb w-60"
            className="h-11"
            menuClassName="max-h-60"
            options={statusOptions}
            value={selectedStatuses}
            onChange={(value) => onStatusChange(value as (string | number)[])}
            multiple={true}
            showAllOption={true}
          />
        </div>

        {/* Region Filter */}
        <div className="flex flex-col gap-2">
          <div className="text-secondary t-sb">
            <Trans i18nKey="create.regionLabel">Region</Trans>
          </div>
          <Dropdown
            rootClassName="t-sb w-60"
            className="h-11"
            options={regionOptions}
            value={region}
            onChange={(value) => {
              const numValue = Number(value)
              let newRegions: number[] = []

              if (numValue === REGION_GLOBAL) {
                newRegions = domainInfos.map((info) => info.domain)
              } else if (numValue === REGION_DATA_DRIVE || numValue === REGION_MESH_CLUSTERING) {
                newRegions = []
              } else {
                newRegions = [numValue]
              }

              onRegionChange(numValue, newRegions)
            }}
          />
        </div>

        <div className="ml-auto flex gap-3">
          {/* Bulk Edit Button */}
          {selectedCount > 0 && (
            <Dropdown
              rootClassName="t-sb"
              className="h-11 px-4"
              options={bulkEditOptions}
              value=""
              onChange={(value) => onBulkEdit(value as 'release')}
              placeholder={t('team.admin.bulkEdit.title')}
              disabled={!allCanRelease}
            />
          )}

          {/* Create New Button */}
          <Link to="/team/create">
            <Button type="secondary">
              <Trans i18nKey="instance.createBtn">Create New</Trans>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
